"use client";

import type { ReactElement } from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useLanguage } from "@/contexts/LanguageContext";
import { SITE_LOGO_ALT, SITE_LOGO_SRC, siteLogoAbsoluteUrl } from "@/lib/siteLogo";
import { GTMEvents, GTMSubEvents, PushDataToGTM } from "@/lib/gtm";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Petromin",
  url: "https://petromin.com",
  logo: siteLogoAbsoluteUrl(),
  description:
    "World-class car care right around the corner. Premium automotive services, genuine parts, and expert care for your vehicle.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+966-11-234-5678",
    contactType: "customer service",
    email: "petrominexpress@petromin.com",
    areaServed: "SA",
  },
  sameAs: [
    "https://www.youtube.com/channel/UCvTM70M17JdvVAhopBgRTEA",
    "https://www.linkedin.com/company/petrominit/",
    "https://www.facebook.com/Petrominit",
    "https://x.com/Petrominit",
    "https://www.instagram.com/petrominit/",
  ],
};

type FooterNavItem = { key: string; href: string, label: string };

const footerSections: {
  titleKey: string;
  ariaLabelKey: string;
  links: FooterNavItem[];
}[] = [
  {
    titleKey: "footer.services",
    ariaLabelKey: "footer.services",
    links: [
      { key: "nav.services", href: "/services", label: "Services" },
      { key: "nav.products", href: "/product", label: "Products" },
      { key: "nav.packages", href: "/packages", label: "Packages" },
      { key: "nav.offers", href: "/offers", label: "Offers" },
    ],
  },
  {
    titleKey: "footer.company",
    ariaLabelKey: "footer.company",
    links: [
      { key: "footer.aboutUs", href: "/about", label: "About Us" },
      { key: "footer.contactUs", href: "/contact", label: "Contact Us" },
      { key: "footer.nearestCenters", href: "/find-workshop", label: "Nearest Centers" },
      { key: "footer.newsBlog", href: "/news", label: "News & Blog" },
    ],
  },
  {
    titleKey: "footer.support",
    ariaLabelKey: "footer.support",
    links: [
      { key: "footer.privacyPolicy", href: "/privacy-policy", label: "Privacy Policy" },
      { key: "footer.termsOfService", href: "/terms-and-conditions", label: "Terms of Service" },
      { key: "footer.cookiePolicy", href: "/cookie-policy", label: "Cookie Policy" },
    ],
  },
];

function SocialIcon({ name }: { name: string }) {
  const icons: Record<string, ReactElement> = {
    youtube: (
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    ),
    linkedin: (
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    ),
    facebook: (
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    ),
    x: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.73 6.227zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
    instagram: (
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    ),
    whatsapp: (
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.883 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    ),
  };
  return (
    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      {icons[name] ?? icons.facebook}
    </svg>
  );
}

type SocialLinkItem = {
  label: string;
  href: string;
  icon: string;
  /** Custom accessible name (e.g. WhatsApp). */
  ariaLabel?: string;
};

const socialLinks: SocialLinkItem[] = [
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCvTM70M17JdvVAhopBgRTEA",
    icon: "youtube",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/petrominit/",
    icon: "linkedin",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/Petrominit",
    icon: "facebook",
  },
  {
    label: "X",
    href: "https://x.com/Petrominit",
    icon: "x",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/petrominit/",
    icon: "instagram",
  },
  {
    label: "WhatsApp",
    href: "https://api.whatsapp.com/send/?phone=966920022776&text&type=phone_number&app_absent=0",
    icon: "whatsapp",
    ariaLabel: "Chat on WhatsApp",
  },
];

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

export function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#0c0a09] text-white" role="contentinfo" id="hide-in-mobile-app">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />

      <div className="h-1 bg-gradient-to-r from-[#DD0428] via-[#E11D48] to-[#FF6B6B]" aria-hidden />

      <div className="border-t border-white/[0.06]">
        <div className="mx-auto max-w-[1320px] px-6 py-14 sm:py-16">
          <div className="flex flex-col gap-14 lg:flex-row lg:items-start lg:gap-16 xl:gap-20">
            {/* Brand */}
            <div className="max-w-md shrink-0 lg:max-w-xs xl:max-w-sm">
              <LocaleLink
                href="/"
                className="inline-flex items-center transition-opacity hover:opacity-90"
                onClick={() => {
                  PushDataToGTM(GTMEvents.DataLayerUpdate, {
                    pageName: "FOOTER",
                    pageCategory: "FOOTER",
                    eventCategory: GTMSubEvents.Click,
                    eventLabel: "FOOTER > LOGO CLICK",
                  });
                }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element -- static brand asset */}
                <img
                  src={SITE_LOGO_SRC}
                  alt={SITE_LOGO_ALT}
                  className="h-11 w-auto max-w-[min(100%,320px)] object-contain object-left"
                  width={320}
                  height={52}
                />
              </LocaleLink>
              <p className="mt-5 text-sm leading-relaxed text-white/65">
                {t("footer.tagline")}
              </p>
              <div className="mt-8 flex flex-col gap-4">
                <a
                  href="tel:+966112345678"
                  className="inline-flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-white"
                  onClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "FOOTER",
                      pageCategory: "FOOTER",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "FOOTER > PHONE CLICK",
                    });
                  }}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-[#BD2226]">
                    <PhoneIcon className="shrink-0" />
                  </span>
                  <span>
                    <span className="block text-xs font-medium uppercase tracking-wide text-white/45">
                      {t("footer.contact").replace(":", "").trim()}
                    </span>
                    <span className="font-medium text-white">8004420020</span>
                  </span>
                </a>
                <a
                  href="mailto:petrominexpress@petromin.com"
                  onClick={() => {
                    PushDataToGTM(GTMEvents.DataLayerUpdate, {
                      pageName: "FOOTER",
                      pageCategory: "FOOTER",
                      eventCategory: GTMSubEvents.Click,
                      eventLabel: "FOOTER > EMAIL CLICK",
                    });
                  }}
                  className="inline-flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-white"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-[#BD2226]">
                    <MailIcon className="shrink-0" />
                  </span>
                  <span>
                    <span className="block text-xs font-medium uppercase tracking-wide text-white/45">
                      {t("footer.email").replace(":", "").trim()}
                    </span>
                    <span className="font-medium text-white">petrominexpress@petromin.com</span>
                  </span>
                </a>
              </div>
              <div className="mt-8 border-t border-white/10 pt-8">
                <div className="flex flex-wrap gap-2.5">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        social.ariaLabel ?? `Follow us on ${social.label}`
                      }
                      onClick={() => {
                        PushDataToGTM(GTMEvents.DataLayerUpdate, {
                          pageName: "FOOTER",
                          pageCategory: "FOOTER",
                          eventCategory: GTMSubEvents.Click,
                          eventLabel: "FOOTER > SOCIAL > " + social.label.toUpperCase(),
                        });
                      }}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-white/80 transition-all hover:border-[#DD0428]/50 hover:bg-[#BD2226]/15 hover:text-white"
                    >
                      <span className="sr-only">{social.label}</span>
                      <SocialIcon name={social.icon} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Link columns */}
            <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3 md:gap-x-6 lg:gap-x-10">
              {footerSections.map((section) => (
                <nav
                  key={section.titleKey}
                  aria-label={t(section.ariaLabelKey)}
                  className="min-w-0"
                >
                  <h2 className="border-b border-white/10 pb-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#BD2226]">
                    {t(section.titleKey)}
                  </h2>
                  <ul className="mt-4 space-y-2.5">
                    {section.links.map((link) => (
                      <li key={link.key}>
                        <LocaleLink
                          href={link.href}
                          onClick={() => {
                            PushDataToGTM(GTMEvents.DataLayerUpdate, {
                              pageName: "FOOTER",
                              pageCategory: "FOOTER",
                              eventCategory: GTMSubEvents.Click,
                              eventLabel: "FOOTER > MENU > " + link.label.toUpperCase(),
                            });
                          }}
                          className="group inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-white"
                        >
                          <span className="border-b border-transparent transition-colors group-hover:border-[#DD0428]/60">
                            {t(link.key)}
                          </span>
                        </LocaleLink>
                      </li>
                    ))}
                  </ul>
                </nav>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-14 w-full border-t border-white/10 pt-10 sm:mt-16 sm:pt-12">
            <p className="w-full text-center text-xs leading-relaxed text-white/55 sm:text-sm">
              {t("footer.copyrightLine").replace(
                "{{year}}",
                String(new Date().getFullYear())
              )}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
